import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY, patchAppUser } from "./appAuthSlice";
import { isPendingRoundActive } from "../hooks/usePendingPlayCountdown";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const apiMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

const adjustCoinBalance = (dispatch, getState, delta) => {
  const current = Number(getState?.()?.appAuth?.user?.COIN || 0);
  if (!Number.isFinite(current)) return;
  dispatch(patchAppUser({ COIN: Math.max(0, current + delta) }));
};

export const fetchBondDiceConstants = createAsyncThunk(
  "bondDice/fetchConstants",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/bond-dice/constants");
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load Bond Dice rules"));
    }
  }
);

export const fetchBondDiceLanding = createAsyncThunk(
  "bondDice/fetchLanding",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/bond-dice/landing");
      return res.data?.pendingRound || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load pending roll"));
    }
  }
);

export const fetchBondDicePool = createAsyncThunk(
  "bondDice/fetchPool",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/bond-dice/pool");
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load investor pool"));
    }
  }
);

export const fetchBondDiceHistory = createAsyncThunk(
  "bondDice/fetchHistory",
  async ({ limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/bond-dice/rounds/history", { params: { limit } });
      return res.data?.rounds || [];
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load dice history"));
    }
  }
);

export const fetchBondDiceInvestors = createAsyncThunk(
  "bondDice/fetchInvestors",
  async ({ limit = 100 } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/bond-dice/investors", { params: { limit } });
      return res.data?.investors || [];
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load investors"));
    }
  }
);

export const fetchBondDicePlayers = createAsyncThunk(
  "bondDice/fetchPlayers",
  async ({ limit = 100 } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/bond-dice/players", { params: { limit } });
      return res.data?.players || [];
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load players"));
    }
  }
);

export const fetchBondDiceQuote = createAsyncThunk(
  "bondDice/fetchQuote",
  async ({ stake, playerPick }, { rejectWithValue }) => {
    try {
      const params = { stake };
      if (playerPick) params.playerPick = playerPick;
      const res = await api.get("/api/bond-dice/quote", { params });
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Invalid stake"));
    }
  }
);

export const bondDiceBuy = createAsyncThunk(
  "bondDice/buy",
  async ({ playerPick, stake }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/bond-dice/buy", { playerPick, stake });
      const round = res.data?.round || null;
      if (round?.stake) adjustCoinBalance(dispatch, getState, -Number(round.stake));
      dispatch(fetchBondDicePool());
      dispatch(fetchBondDiceInvestors({ limit: 100 }));
      dispatch(fetchBondDicePlayers({ limit: 100 }));
      return round;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not reserve dice ticket"));
    }
  }
);

export const bondDiceRoll = createAsyncThunk(
  "bondDice/roll",
  async ({ roundId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/bond-dice/roll", { roundId });
      const round = res.data?.round || null;
      if (round?.status === "Won") {
        const net = Number(round.playerNetPayout || 0);
        if (net > 0) adjustCoinBalance(dispatch, getState, net);
      }
      dispatch(fetchBondDicePool());
      dispatch(fetchBondDiceInvestors({ limit: 100 }));
      dispatch(fetchBondDicePlayers({ limit: 100 }));
      return round;
    } catch (error) {
      const msg = apiMessage(error, "Could not roll dice");
      if (/expired/i.test(msg)) {
        const stake = Number(getState?.()?.bondDice?.pendingRound?.stake || 0);
        if (stake > 0) adjustCoinBalance(dispatch, getState, stake);
      }
      return rejectWithValue(msg);
    }
  }
);

export const bondDiceCancel = createAsyncThunk(
  "bondDice/cancel",
  async ({ roundId, stake = 0 }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/bond-dice/cancel", { roundId });
      if (stake > 0) adjustCoinBalance(dispatch, getState, Number(stake));
      dispatch(fetchBondDicePool());
      dispatch(fetchBondDiceInvestors({ limit: 100 }));
      dispatch(fetchBondDicePlayers({ limit: 100 }));
      return res.data?.round || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not cancel ticket"));
    }
  }
);

export const bondDiceInvest = createAsyncThunk(
  "bondDice/invest",
  async ({ amount }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/bond-dice/invest", { amount });
      const invested = Number(res.data?.amount || 0);
      if (invested > 0) adjustCoinBalance(dispatch, getState, -invested);
      dispatch(fetchBondDicePool());
      dispatch(fetchBondDiceInvestors({ limit: 100 }));
      dispatch(fetchBondDicePlayers({ limit: 100 }));
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Invest failed"));
    }
  }
);

export const bondDiceInvestorCashout = createAsyncThunk(
  "bondDice/investorCashout",
  async ({ amount } = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/bond-dice/investor/cashout", { amount });
      const withdrawn = Number(res.data?.amount || 0);
      if (withdrawn > 0) adjustCoinBalance(dispatch, getState, withdrawn);
      dispatch(fetchBondDicePool());
      dispatch(fetchBondDiceInvestors({ limit: 100 }));
      dispatch(fetchBondDicePlayers({ limit: 100 }));
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Cash out failed"));
    }
  }
);

export const bondDiceInvestorToggleActive = createAsyncThunk(
  "bondDice/investorToggleActive",
  async ({ isActive }, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/bond-dice/investor/toggle-active", { isActive });
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not update investor status"));
    }
  }
);

const initialState = {
  constants: null,
  pendingRound: null,
  lastSettledRound: null,
  pool: null,
  players: [],
  investors: [],
  history: [],
  quote: null,
  loading: false,
  loadingPool: false,
  loadingPlayers: false,
  loadingInvestors: false,
  actionLoading: false,
  investorActionLoading: false,
  error: null,
};

const bondDiceSlice = createSlice({
  name: "bondDice",
  initialState,
  reducers: {
    setBondDicePendingRound: (state, { payload }) => {
      state.pendingRound = payload || null;
    },
    setBondDiceLastSettled: (state, { payload }) => {
      state.lastSettledRound = payload || null;
    },
    clearBondDicePendingRound: (state) => {
      state.pendingRound = null;
    },
    clearBondDiceLastSettled: (state) => {
      state.lastSettledRound = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBondDiceConstants.fulfilled, (state, { payload }) => {
        state.constants = payload;
      })
      .addCase(fetchBondDiceLanding.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBondDiceLanding.fulfilled, (state, { payload }) => {
        state.loading = false;
        const ttlMs = state.constants?.pendingPlayTtlMs || 60 * 1000;
        state.pendingRound =
          payload?.status === "Pending" && isPendingRoundActive(payload, ttlMs) ? payload : null;
      })
      .addCase(fetchBondDiceLanding.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(fetchBondDicePool.fulfilled, (state, { payload }) => {
        state.pool = payload;
        state.loadingPool = false;
      })
      .addCase(fetchBondDicePlayers.pending, (state) => {
        state.loadingPlayers = true;
      })
      .addCase(fetchBondDicePlayers.fulfilled, (state, { payload }) => {
        state.players = payload || [];
        state.loadingPlayers = false;
      })
      .addCase(fetchBondDicePlayers.rejected, (state) => {
        state.loadingPlayers = false;
      })
      .addCase(fetchBondDiceInvestors.pending, (state) => {
        state.loadingInvestors = true;
      })
      .addCase(fetchBondDiceInvestors.fulfilled, (state, { payload }) => {
        state.investors = payload || [];
        state.loadingInvestors = false;
      })
      .addCase(fetchBondDiceInvestors.rejected, (state) => {
        state.loadingInvestors = false;
      })
      .addCase(fetchBondDiceQuote.fulfilled, (state, { payload }) => {
        state.quote = payload;
      })
      .addCase(bondDiceBuy.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(bondDiceBuy.fulfilled, (state, { payload }) => {
        state.actionLoading = false;
        state.pendingRound = payload;
      })
      .addCase(bondDiceBuy.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(bondDiceRoll.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(bondDiceRoll.fulfilled, (state, { payload }) => {
        state.actionLoading = false;
        state.pendingRound = null;
        state.lastSettledRound = payload;
      })
      .addCase(bondDiceRoll.rejected, (state, { payload }) => {
        state.actionLoading = false;
        if (typeof payload === "string" && /expired/i.test(payload)) {
          state.pendingRound = null;
        }
      })
      .addCase(bondDiceCancel.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(bondDiceCancel.fulfilled, (state) => {
        state.actionLoading = false;
        state.pendingRound = null;
      })
      .addCase(bondDiceCancel.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(bondDiceInvest.pending, (state) => {
        state.investorActionLoading = true;
      })
      .addCase(bondDiceInvest.fulfilled, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(bondDiceInvest.rejected, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(bondDiceInvestorCashout.pending, (state) => {
        state.investorActionLoading = true;
      })
      .addCase(bondDiceInvestorCashout.fulfilled, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(bondDiceInvestorCashout.rejected, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(bondDiceInvestorToggleActive.pending, (state) => {
        state.investorActionLoading = true;
      })
      .addCase(bondDiceInvestorToggleActive.fulfilled, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(bondDiceInvestorToggleActive.rejected, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(fetchBondDiceHistory.fulfilled, (state, { payload }) => {
        state.history = payload || [];
      });
  },
});

export const {
  setBondDicePendingRound,
  setBondDiceLastSettled,
  clearBondDicePendingRound,
  clearBondDiceLastSettled,
} = bondDiceSlice.actions;

export const selectBondDiceConstants = (state) => state.bondDice?.constants;
export const selectBondDicePendingRound = (state) => state.bondDice?.pendingRound;
export const selectBondDiceLastSettled = (state) => state.bondDice?.lastSettledRound;
export const selectBondDicePool = (state) => state.bondDice?.pool;
export const selectBondDicePlayers = (state) => state.bondDice?.players || [];
export const selectBondDiceInvestors = (state) => state.bondDice?.investors || [];
export const selectBondDiceLoadingPlayers = (state) => state.bondDice?.loadingPlayers;
export const selectBondDiceLoadingInvestors = (state) => state.bondDice?.loadingInvestors;
export const selectBondDiceQuote = (state) => state.bondDice?.quote;
export const selectBondDiceLoading = (state) => state.bondDice?.loading;
export const selectBondDiceActionLoading = (state) => state.bondDice?.actionLoading;
export const selectBondDiceInvestorActionLoading = (state) => state.bondDice?.investorActionLoading;
export const selectBondDiceHistory = (state) => state.bondDice?.history || [];

export default bondDiceSlice.reducer;
